"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFirebaseContext = exports.FirebaseProvider = exports.FirebaseContext = exports.FirebaseAuthError = void 0;
const react_1 = __importStar(require("react"));
const auth_1 = require("firebase/auth");
const TEST_USER = { email: 'test@example.com' };
const FIREBASE_LOCALIZED_ERROR_MAP = new Map([
    [
        auth_1.AuthErrorCodes.INVALID_PASSWORD,
        "We couldn't find an account with that email address and password. Please double-check and try again.",
    ],
    [
        auth_1.AuthErrorCodes.USER_DELETED,
        "We couldn't find an account with that email address or password. Please double-check and try again.",
    ],
    [
        auth_1.AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER,
        'Too many login attempts. Please try again later or reset your password.',
    ],
]);
const localizeFirebaseError = (error) => {
    const errorByCode = FIREBASE_LOCALIZED_ERROR_MAP.get(error.code);
    if (errorByCode) {
        return errorByCode;
    }
    const customMessage = error.customData?.message ?? '';
    for (const [key, value] of FIREBASE_LOCALIZED_ERROR_MAP.entries()) {
        if (customMessage.includes(key)) {
            return value;
        }
    }
    return undefined;
};
class FirebaseAuthError extends Error {
    code;
    constructor(message, code = '') {
        super(message);
        this.name = this.constructor.name;
        this.code = code;
    }
}
exports.FirebaseAuthError = FirebaseAuthError;
exports.FirebaseContext = (0, react_1.createContext)({
    googleUser: null,
    loading: true,
    login: undefined,
    logout: undefined,
    updatePassword: undefined,
});
/**
 *
 * @deprecated use FirebaseProvider from ui-kit instead
 */
const FirebaseProvider = ({ children, auth }) => {
    const [googleUser, setGoogleUser] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    const impersonateToken = searchParams.get('impersonateToken');
    (0, react_1.useEffect)(() => {
        const impersonateOneUser = async () => {
            if (impersonateToken) {
                await (0, auth_1.signOut)(auth);
                (0, auth_1.signInWithCustomToken)(auth, impersonateToken);
                window.localStorage.setItem('impersonating', 'true');
            }
        };
        impersonateOneUser();
    }, [impersonateToken]);
    (0, auth_1.onAuthStateChanged)(auth, async (user) => {
        if (user) {
            setGoogleUser(user);
        }
        setLoading(false);
    });
    const login = async (credentials) => {
        try {
            await (0, auth_1.setPersistence)(auth, auth_1.browserLocalPersistence);
            await (0, auth_1.signInWithEmailAndPassword)(auth, credentials.email, credentials.password);
        }
        catch (error) {
            const localizedError = localizeFirebaseError(error);
            if (localizedError) {
                throw new FirebaseAuthError(localizedError, error.code);
            }
            throw error;
        }
    };
    const updatePassword = async (password) => {
        try {
            if (!!googleUser) {
                await (0, auth_1.updatePassword)(googleUser, password);
            }
        }
        catch (error) {
            throw error;
        }
    };
    const reauthenticateUser = async (password) => {
        try {
            if (!!googleUser && googleUser.email) {
                const credential = auth_1.EmailAuthProvider.credential(googleUser.email, password);
                await (0, auth_1.reauthenticateWithCredential)(googleUser, credential);
            }
        }
        catch (error) {
            const localizedError = localizeFirebaseError(error);
            if (localizedError) {
                throw new FirebaseAuthError(localizedError, error.code);
            }
            throw error;
        }
    };
    const logout = async (redirectUrl) => {
        await (0, auth_1.signOut)(auth);
        window.localStorage.removeItem('impersonating');
        if (redirectUrl) {
            window.location.replace(redirectUrl);
        }
        else {
            window.location.assign('/');
        }
    };
    return (react_1.default.createElement(exports.FirebaseContext.Provider, { value: {
            googleUser: !!window.Cypress ? TEST_USER : googleUser,
            loading,
            login,
            logout,
            updatePassword,
            reauthenticateUser,
        } }, children));
};
exports.FirebaseProvider = FirebaseProvider;
const useFirebaseContext = () => (0, react_1.useContext)(exports.FirebaseContext);
exports.useFirebaseContext = useFirebaseContext;
